import React, { useCallback, useEffect, useState } from 'react';
import { Bot } from 'lucide-react';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { AiChatPanel } from './AiChatPanel';
import { TradeProposalsPanel } from './TradeProposalsPanel';
import { ConversationHistoryRail } from './ConversationHistoryRail';

/**
 * AI assistant page — two-column layout: conversation history rail on the
 * left, current chat on the right. Selecting a row in the rail loads that
 * conversation into the panel; the "New" button starts a fresh one.
 *
 * The history rail refetches whenever:
 *  - the user picks a different conversation (so its own selection state syncs),
 *  - a turn completes that may have created/disabled a schedule, or
 *  - a brand-new conversation was just committed (its id arrived on `done`).
 */
/** Read `conversation=<id>` from the dashboard hash so deep links land on the
 *  originating thread. Format: `#/dashboard/ai-assistant?conversation=<id>`.
 *  Returns null if absent. Centralised so the mount effect and hashchange
 *  listener stay in lockstep. */
function readConversationIdFromHash(): string | null {
  const queryStart = window.location.hash.indexOf('?');
  if (queryStart < 0) return null;
  const params = new URLSearchParams(window.location.hash.slice(queryStart + 1));
  const id = params.get('conversation');
  return id && id.length > 0 ? id : null;
}

/** Mirror the current selection back into the URL so the hash and the
 *  rendered thread can't drift apart — `View conversation` links, the
 *  browser back button, and copy-paste-the-URL share workflows all rely on
 *  the hash reflecting reality. Uses replaceState so it doesn't pollute the
 *  back-button stack on every row click. */
function writeConversationIdToHash(id: string | null): void {
  const hash = window.location.hash;
  // Preserve the tab path (everything before `?`) — only the query portion
  // changes here.
  const [path, queryPart] = hash.split('?');
  const params = new URLSearchParams(queryPart ?? '');
  if (id) params.set('conversation', id);
  else params.delete('conversation');
  const next = params.toString();
  const newHash = next ? `${path}?${next}` : path;
  if (newHash === hash) return;   // no-op — avoids spurious hashchange events
  window.history.replaceState(null,
    '',
    window.location.pathname + window.location.search + newHash);
}

export const AiAssistantPage: React.FC = () => {
  const [context, setContext] = useState('general');
  // Seed selectedId from the URL on mount so `View conversation` links from
  // the trade proposals panel (and the cross-conversation Schedules tab) land
  // on the originating thread instead of an empty chat.
  const [selectedId, setSelectedId] = useState<string | null>(() => readConversationIdFromHash());
  const [railRefreshKey, setRailRefreshKey] = useState(0);

  // Browser back/forward + paste-in URLs — keep selection in sync with the
  // hash when the user navigates without remounting the page.
  useEffect(() => {
    const onHash = () => setSelectedId(readConversationIdFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Mirror selection BACK into the URL whenever it changes — without this,
  // picking a different conversation in the rail leaves a stale
  // `?conversation=<id>` in the URL and a follow-up "View conversation"
  // link to that same id would be a no-op (hash unchanged → no hashchange,
  // no re-select). Two-way binding keeps the hash truthy.
  useEffect(() => {
    writeConversationIdToHash(selectedId);
  }, [selectedId]);

  /** Context drives the system-prompt framing — switching contexts inside a
   *  selected conversation would mix two framings on one thread. Always
   *  start fresh when the user changes context. */
  const onContextChange = useCallback((next: string) => {
    setContext(next);
    setSelectedId(null);
  }, []);

  const onConversationChanged = useCallback((id: string | null) => {
    setSelectedId(id);
    setRailRefreshKey((k) => k + 1);
  }, []);

  const onTurnComplete = useCallback(() => {
    setRailRefreshKey((k) => k + 1);
  }, []);

  /** Adopt the loaded conversation's stored context so the dropdown matches
   *  the framing the thread was created under and the next turn doesn't
   *  silently shift system-prompt framings. */
  const onConversationLoaded = useCallback((c: { context: string }) => {
    if (c.context && c.context !== context) setContext(c.context);
  }, [context]);

  return (
    // Stacks on phones (rail on top, capped height), side-by-side from lg up —
    // the StrategyDashboard collapsing-rail pattern.
    <div className="flex flex-col lg:flex-row h-full">
      <ConversationHistoryRail
        selectedId={selectedId}
        onSelect={setSelectedId}
        refreshKey={railRefreshKey}
      />

      <div className="flex-1 flex flex-col min-w-0 p-4 gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-semibold">AI Assistant</h1>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Context</span>
            <Select value={context} onValueChange={onContextChange}>
              <SelectTrigger className="h-8 w-[160px]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover/100 backdrop-blur-md z-[100]">
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="paper">Paper trading</SelectItem>
                <SelectItem value="live">Live trading</SelectItem>
                <SelectItem value="strategy">Strategies</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Pending trade proposals — renders nothing when there are none. */}
        <TradeProposalsPanel />

        {/* The chat. Keyed on (selectedId, context) so changing either picks
            up a clean state — switching context to 'strategy' should start
            a fresh thread, not append to the previous one. */}
        <div className="flex-1 min-h-0">
          <AiChatPanel
            key={`${selectedId ?? 'new'}|${context}`}
            context={context}
            conversationId={selectedId}
            onConversationChanged={onConversationChanged}
            onTurnComplete={onTurnComplete}
            onConversationLoaded={onConversationLoaded}
          />
        </div>
      </div>
    </div>
  );
};

export default AiAssistantPage;
